import React, { useCallback, useEffect, useState } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Loader2, X } from 'lucide-react';
import AddPractitionerForm from '@/components/forms/AddPractitionerForm';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { practitionerService } from '@/services/practitionerService';
import { toast } from '@/hooks/use-toast';
import {
  CreatePractitionerRequest,
  Practitioner,
  UpdatePractitionerRequest,
} from '@/services/types';
import { getDEAValidationError } from '@/lib/validators';

export const practitionerSchema = z.object({
  first_name: z
    .string()
    .trim()
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be at most 50 characters'),
  last_name: z
    .string()
    .trim()
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be at most 50 characters'),
  full_name: z
    .string()
    .trim()
    .min(2, 'Full name must be at least 2 characters')
    .max(100, 'Full name must be at most 100 characters')
    .optional(),
  credentials: z.string().trim().min(2, 'Credential is required').optional().or(z.literal('')),
  email: z.string().trim().email('Please enter a valid email address').min(1, 'Email is required'),
  license_number: z
    .string()
    .trim()
    .max(30, 'License number must be at most 30 characters')
    .optional()
    .or(z.literal('')),
  npi_number: z
    .string()
    .trim()
    .regex(/^\d{10}$/, 'NPI number must be 10 digits'),
  dea_number: z
    .string()
    .trim()
    .optional()
    .refine(
      value => {
        if (!value || value === '') return true; // Allow empty (optional field)
        const error = getDEAValidationError(value);
        return error === null;
      },
      value => ({
        message: getDEAValidationError(value || '') || 'Invalid DEA number',
      })
    ),
  clinic_id: z.string().trim().optional().or(z.literal('')),
  fax: z.string().trim().max(20, 'Fax must be at most 20 characters').optional().or(z.literal('')),
  phone: z
    .string()
    .trim()
    .max(20, 'Phone must be at most 20 characters')
    .optional()
    .or(z.literal('')),
  address_line_1: z
    .string()
    .trim()
    .min(5, 'Please enter a valid address')
    .max(100, 'Address must be at most 100 characters'),
  address_line_2: z
    .string()
    .trim()
    .max(100, 'Address line 2 must be at most 100 characters')
    .optional()
    .or(z.literal('')),
  city: z
    .string()
    .trim()
    .min(2, 'Please enter a valid city')
    .max(50, 'City must be at most 50 characters'),
  state: z.string().trim().length(2, 'Please select a state'),
  postal_code: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'Postal code must be 5 digits'),
});

type PractitionerFormData = z.infer<typeof practitionerSchema>;

interface AddPractitionerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (practitioner: any) => void;
  clinicId: string;
  practitionerId?: string;
  isEdit?: boolean;
}

const AddPractitionerSheet = ({
  open,
  onOpenChange,
  onSuccess,
  clinicId,
  practitionerId,
  isEdit = false,
}: AddPractitionerSheetProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [practitioner, setPractitioner] = useState<Practitioner | null>(null);
  const [npiValidation, setNpiValidation] = useState<{
    isValidating: boolean;
    exists: boolean;
    practitioner?: { id: string; name: string; npi: string };
  }>({
    isValidating: false,
    exists: false,
  });
  const [emailValidation, setEmailValidation] = useState<{
    isValidating: boolean;
    exists: boolean;
    practitioner?: { id: string; name: string; npi: string; email: string };
  }>({
    isValidating: false,
    exists: false,
  });
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState<'npi' | 'email'>('npi');
  const [duplicatePractitioner, setDuplicatePractitioner] = useState<{
    id: string;
    name: string;
    npi: string;
    email?: string;
  } | null>(null);
  const [isLinking, setIsLinking] = useState(false);

  const form = useForm<PractitionerFormData>({
    resolver: zodResolver(practitionerSchema),
    defaultValues: {
      first_name:  '',
      last_name: '',
      full_name: '',
      email: '',
      credentials: '',
      license_number: '',
      npi_number: '',
      dea_number: '',
      clinic_id: clinicId,
      fax: '',
      phone: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      postal_code: '',
    },
  });

  const handleLinkExistingPractitioner = async () => {
    if (!duplicatePractitioner) return;
    try {
      setIsLinking(true);
      const response: any = await practitionerService.linkToClinic(
        clinicId,
        duplicatePractitioner.id
      );

      if (response?.alreadyLinked) {
        toast({
          title: 'Already linked',
          description: 'Practitioner is already linked to this clinic.',
          variant: 'success',
        });
      } else {
        toast({
          title: 'Success',
          description: 'Practitioner has been linked to this clinic.',
          variant: 'success',
        });
      }

      onSuccess(response?.practitioner || duplicatePractitioner);
      form.reset();
      setLinkDialogOpen(false);
      setDuplicatePractitioner(null);
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description:
          error.message || 'Failed to link practitioner to this clinic. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLinking(false);
    }
  };

  const handleSubmit = async (practitionerData: PractitionerFormData) => {
    try {
      setIsSubmitting(true);

      // Skip NPI/email validation if editing (NPI is read-only)
      if (!isEdit) {
        if (npiValidation.isValidating || emailValidation.isValidating) {
          toast({
            title: 'Please wait',
            description: 'Validation is in progress. Please wait a moment and try again.',
            variant: 'destructive',
          });
          return;
        }

        // Check if NPI already exists
        if (npiValidation.exists) {
          setDuplicateSource('npi');
          setLinkDialogOpen(true);
          return;
        }

        // Check if email already exists
        if (emailValidation.exists) {
          setDuplicateSource('email');
          setDuplicatePractitioner(emailValidation.practitioner || null);
          setLinkDialogOpen(true);
          return;
        }
      }

      if (isEdit && practitionerId) {
        // Update existing practitioner
        // Note: NPI, DEA, and License Number are excluded as they require re-verification
        const updateData: UpdatePractitionerRequest = {
          first_name: practitionerData.first_name.trim(),
          last_name: practitionerData.last_name.trim(),
          full_name: `${practitionerData.first_name} ${practitionerData.last_name}`.trim(),
          credentials: practitionerData.credentials || undefined,
          email: practitionerData.email,
          // Regulated identifiers excluded: npi_number, dea_number, license_number
          address_line_1: practitionerData.address_line_1,
          city: practitionerData.city,
          state: practitionerData.state,
          postal_code: practitionerData.postal_code,
          // Optional fields
          fax: practitionerData.fax || undefined,
          phone: practitionerData.phone || undefined,
          address_line_2: practitionerData.address_line_2 || undefined,
        };

        const updatedPractitioner = await practitionerService.updatePractitioner(
          practitionerId,
          updateData
        );

        toast({
          title: 'Success',
          description: 'Practitioner has been successfully updated.',
          variant: 'success',
        });

        // Call the callback to refresh the parent component
        onSuccess(updatedPractitioner);
      } else {
        // Create new practitioner
        const apiData: CreatePractitionerRequest = {
          first_name: practitionerData.first_name,
          last_name: practitionerData.last_name,
          full_name: `${practitionerData.first_name} ${practitionerData.last_name}`.trim(),
          credentials: practitionerData.credentials || undefined,
          email: practitionerData.email,
          npi_number: practitionerData.npi_number,
          clinic_id: clinicId,
          address_line_1: practitionerData.address_line_1,
          city: practitionerData.city,
          state: practitionerData.state,
          postal_code: practitionerData.postal_code,
          // Optional fields
          license_number: practitionerData.license_number || undefined,
          dea_number: practitionerData.dea_number || undefined,
          fax: practitionerData.fax || undefined,
          phone: practitionerData.phone || undefined,
          address_line_2: practitionerData.address_line_2 || undefined,
        };

        const createdPractitioner = await practitionerService.createPractitioner(clinicId, apiData);

        toast({
          title: 'Success',
          description:
            'Your Practitioner creation request is being send to Novamed.',
          variant: 'success',
        });

        // Call the callback to refresh the parent component
        onSuccess(createdPractitioner);
      }
    

      // Reset form and close sheet
      form.reset();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error saving practitioner:', error);

      toast({
        title: 'Error',
        description:
          error.message ||
          `Failed to ${isEdit ? 'update' : 'create'} practitioner. Please try again.`,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'first_name' || name === 'last_name') {
        const firstName = value.first_name || '';
        const lastName = value.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        form.setValue('full_name', fullName);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

    // Debounced NPI validation
    const validateNpi = useCallback(
      async (npi: string) => {
        if (!npi || npi.length !== 10 || !/^\d{10}$/.test(npi)) {
          setNpiValidation({ isValidating: false, exists: false });
          setDuplicatePractitioner(null);
          setLinkDialogOpen(false);
          return;
        }
  
        // Skip validation if editing and NPI hasn't changed
        if (isEdit && practitioner?.npi === npi) {
          setNpiValidation({ isValidating: false, exists: false });
          form.clearErrors('npi_number');
          setDuplicatePractitioner(null);
          setLinkDialogOpen(false);
          return;
        }
  
        setNpiValidation(prev => ({ ...prev, isValidating: true }));
  
        try {
          const result = await practitionerService.checkPractitionerNpiExists(npi);
          setNpiValidation({
            isValidating: false,
            exists: result.exists,
            practitioner: result.practitioner,
          });
  
          if (result.exists) {
            form.setError('npi_number', {
              type: 'manual',
              message: `A practitioner with NPI ${npi} already exists: ${result.practitioner?.name}`,
            });
            setDuplicateSource('npi');
            setDuplicatePractitioner(result.practitioner || null);
            if (!isEdit) {
              setLinkDialogOpen(true);
            }
          } else {
            form.clearErrors('npi_number');
            setDuplicatePractitioner(null);
            setLinkDialogOpen(false);
          }
        } catch (error) {
          console.error('NPI validation error:', error);
          setNpiValidation({ isValidating: false, exists: false });
          setDuplicatePractitioner(null);
          setLinkDialogOpen(false);
        }
      },
      [form, isEdit, practitioner]
    );
  
    // Debounce NPI validation
    useEffect(() => {
      const npi = form.watch('npi_number');
      const timeoutId = setTimeout(() => {
        if (npi && npi.length === 10) {
          // Skip validation if editing and NPI hasn't changed
          if (isEdit && practitioner?.npi === npi) {
            setNpiValidation({ isValidating: false, exists: false });
            form.clearErrors('npi_number');
            setDuplicatePractitioner(null);
            setLinkDialogOpen(false);
          } else {
            validateNpi(npi);
          }
        } else {
          setNpiValidation({ isValidating: false, exists: false });
          setDuplicatePractitioner(null);
          setLinkDialogOpen(false);
        }
      }, 500); // 500ms delay
  
    return () => clearTimeout(timeoutId);
  }, [form.watch('npi_number'), validateNpi, isEdit, practitioner]);

  // Debounced email validation
  const validateEmail = useCallback(
    async (email: string) => {
      const trimmedEmail = (email || '').toLowerCase().trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
        setEmailValidation({ isValidating: false, exists: false });
        return;
      }

      // Skip validation if editing and email hasn't changed
      if (isEdit && practitioner?.email === trimmedEmail) {
        setEmailValidation({ isValidating: false, exists: false });
        form.clearErrors('email');
        return;
      }

      setEmailValidation(prev => ({ ...prev, isValidating: true }));

      try {
        const result = await practitionerService.checkPractitionerEmailExists(trimmedEmail);
        setEmailValidation({
          isValidating: false,
          exists: result.exists,
          practitioner: result.practitioner,
        });

        if (result.exists) {
          form.setError('email', {
            type: 'manual',
            message: `A practitioner with this email already exists: ${result.practitioner?.name}`,
          });
          if (!isEdit) {
            setDuplicateSource('email');
            setDuplicatePractitioner(result.practitioner || null);
            setLinkDialogOpen(true);
          }
        } else {
          form.clearErrors('email');
        }
      } catch (error) {
        console.error('Email validation error:', error);
        setEmailValidation({ isValidating: false, exists: false });
      }
    },
    [form, isEdit, practitioner]
  );

  // Debounce email validation
  useEffect(() => {
    const email = form.watch('email');
    const trimmedEmail = (email || '').toLowerCase().trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const timeoutId = setTimeout(() => {
      if (trimmedEmail && emailRegex.test(trimmedEmail)) {
        // Skip validation if editing and email hasn't changed
        if (isEdit && practitioner?.email === trimmedEmail) {
          setEmailValidation({ isValidating: false, exists: false });
          form.clearErrors('email');
        } else {
          validateEmail(trimmedEmail);
        }
      } else {
        setEmailValidation({ isValidating: false, exists: false });
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [form.watch('email'), validateEmail, isEdit, practitioner]);

  const fetchPractitioner = async () => {
    if (!practitionerId) return;
    try {
      setIsLoading(true);
      const response = await practitionerService.getPractitionerById(clinicId, practitionerId);
      const practitionerData = response.data?.practitioner;
      setPractitioner(practitionerData);
      
      if (practitionerData) {
        form.reset({
          first_name: practitionerData.first_name || '',
          last_name: practitionerData.last_name || '',
          full_name: practitionerData.full_name || '',
          email: practitionerData.email || '',
          npi_number: practitionerData.npi || '',
          license_number: practitionerData.license_number || '',
          dea_number: practitionerData.dea || '',
          clinic_id: clinicId,
          fax: practitionerData.fax || '',
          phone: practitionerData.phone || '',
          address_line_1: practitionerData.office_address_line_1 || '',
          address_line_2: practitionerData.office_address_line_2 || '',
          city: practitionerData.office_city || '',
          state: practitionerData?.stateInfo?.code || '',
          postal_code: practitionerData.office_postal_code || '',
          credentials: practitionerData.credentials || '',
        });
      }
    } catch (error) {
      console.error('Error fetching practitioner:', error);
      toast({
        title: 'Error',
        description: 'Failed to load practitioner data.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit && practitionerId && open) {
      fetchPractitioner();
    } else if (!isEdit) {
      form.reset({
        first_name: '',
        last_name: '',
        full_name: '',
        email: '',
        credentials: '',
        license_number: '',
        npi_number: '',
        dea_number: '',
        clinic_id: clinicId,
        fax: '',
        phone: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        postal_code: '',
      });
      setEmailValidation({ isValidating: false, exists: false });
      setNpiValidation({ isValidating: false, exists: false });
      setDuplicatePractitioner(null);
      setLinkDialogOpen(false);
    }
  }, [isEdit, practitionerId, open]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        className="w-full !max-w-[640px] rounded-l-2xl p-0 flex flex-col gap-0 [&>button]:hidden"
        onInteractOutside={(e) => {
          // Prevent closing when clicking on Google Places autocomplete dropdown
          const target = e.target as HTMLElement;
          if (target?.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          // Prevent closing when clicking on Google Places autocomplete dropdown
          const target = e.target as HTMLElement;
          if (target?.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">
            {isEdit ? 'Edit Practitioner' : 'Add Practitioner'}
          </h2>
          <button
            onClick={() => onOpenChange(false)}
            className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
          >
            <X className="h-5 w-5 text-slate-600" />
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-6 py-6">
                <AddPractitionerForm form={form} onSubmit={handleSubmit} isEdit={isEdit} />
              </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
                className="text-slate-600 hover:text-slate-900"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-brand-teal hover:bg-brand-teal/90 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isEdit ? 'Updating...' : 'Creating...'}
                  </>
                ) : isEdit ? (
                  'Update Practitioner'
                ) : (
                  'Add Practitioner'
                )}
              </Button>
            </div>
          </form>
        </Form>
        )}
      </SheetContent>
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Practitioner already exists</DialogTitle>
            <DialogDescription className="pt-4">
              A practitioner named{' '}
              <span className="font-semibold">{duplicatePractitioner?.name}</span>{' '}
              {duplicateSource === 'email' ? (
                <>
                  with email{' '}
                  <span className="font-semibold">{duplicatePractitioner?.email}</span>
                </>
              ) : (
                <>
                  with NPI{' '}
                  <span className="font-semibold">{duplicatePractitioner?.npi}</span>
                </>
              )}{' '}
              already exists in the system. Would you like to link this practitioner to the clinic?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setLinkDialogOpen(false)}
              disabled={isLinking}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-brand-teal hover:bg-brand-teal/90 text-white"
              onClick={handleLinkExistingPractitioner}
              disabled={isLinking}
            >
              {isLinking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Linking...
                </>
              ) : (
                'Link Practitioner'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
};

export default AddPractitionerSheet;
